import { baseApi } from "@/shared/api/baseApi";
import { encryptedSetItem, encryptedRemoveItem } from "@/shared/lib/encryptedStorage";

import { setUser, clearUser, USER_KEY } from "../slices/authSlice";
import type { SignInResponse, AuthUser } from "../types";

/* ═══════════════════════════════════════════════════════════════
   Request DTOs
═══════════════════════════════════════════════════════════════ */
interface SignInRequest {
  email: string;
  senha: string;
}

interface EsqueciSenhaRequest {
  email: string;
}

interface VerificarMfaRequest {
  challengeToken: string;
  codigo: string;
}

interface VerificarRecoveryCodeRequest {
  challengeToken: string;
  codigo: string;
}

interface RedefinirSenhaRequest {
  challengeToken: string;
  novaSenha: string;
}

interface DefinirSenhaRequest {
  challengeToken: string;
  novaSenha: string;
}

export interface PoliticaSenhaResponse {
  tamanhoMinimo: number;
  letrasMaiusculas: boolean;
  letrasMinusculas: boolean;
  numeros: boolean;
  caracteresEspeciais: boolean;
}

/* ═══════════════════════════════════════════════════════════════
   Helper: busca /me e salva no Redux + localStorage criptografado
═══════════════════════════════════════════════════════════════ */
async function fetchAndPersistUser(
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  baseQuery: any,
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  dispatchFn: any,
) {
  const meResult = await baseQuery("/api/auth/me");
  if (meResult.data) {
    const user = meResult.data as AuthUser;
    dispatchFn(setUser(user));
    await encryptedSetItem(USER_KEY, user);
  }
}

/* ═══════════════════════════════════════════════════════════════
   Endpoints
═══════════════════════════════════════════════════════════════ */
export const authApi = baseApi.injectEndpoints({
  endpoints: (builder) => ({
    /** POST /api/auth/sign-in — tokens vêm em httpOnly cookies */
    signIn: builder.mutation<SignInResponse, SignInRequest>({
      queryFn: async (credentials, { dispatch }, _extraOptions, baseQuery) => {
        const result = await baseQuery({
          url: "/api/auth/sign-in",
          method: "POST",
          body: credentials,
        });

        if (result.error) return { error: result.error };

        const response = result.data as SignInResponse;

        // Cookies já foram setados pelo backend; buscar dados do usuário
        if (response.status === "AUTENTICADO") {
          await fetchAndPersistUser(baseQuery, dispatch);
        }

        return { data: response };
      },
    }),

    /** POST /api/auth/esqueci-senha */
    esqueciSenha: builder.mutation<void, EsqueciSenhaRequest>({
      query: (body) => ({
        url: "/api/auth/esqueci-senha",
        method: "POST",
        body,
      }),
    }),

    /** POST /api/auth/verificar-mfa — tokens vêm em httpOnly cookies */
    verificarMfa: builder.mutation<SignInResponse, VerificarMfaRequest>({
      queryFn: async (body, { dispatch }, _extraOptions, baseQuery) => {
        const result = await baseQuery({
          url: "/api/auth/verificar-mfa",
          method: "POST",
          body,
        });

        if (result.error) return { error: result.error };

        const response = result.data as SignInResponse;

        if (response.accessToken || response.status === "AUTENTICADO") {
          await fetchAndPersistUser(baseQuery, dispatch);
        }

        return { data: response };
      },
    }),

    /** POST /api/auth/verificar-recovery-code — login via recovery code */
    verificarRecoveryCode: builder.mutation<SignInResponse, VerificarRecoveryCodeRequest>({
      queryFn: async (body, { dispatch }, _extraOptions, baseQuery) => {
        const result = await baseQuery({
          url: "/api/auth/verificar-recovery-code",
          method: "POST",
          body,
        });

        if (result.error) return { error: result.error };

        const response = result.data as SignInResponse;

        if (response.accessToken || response.status === "AUTENTICADO") {
          await fetchAndPersistUser(baseQuery, dispatch);
        }

        return { data: response };
      },
    }),

    /** POST /api/auth/redefinir-senha */
    redefinirSenha: builder.mutation<void, RedefinirSenhaRequest>({
      query: (body) => ({
        url: "/api/auth/redefinir-senha",
        method: "POST",
        body,
      }),
    }),

    /** POST /api/auth/definir-senha */
    definirSenha: builder.mutation<void, DefinirSenhaRequest>({
      query: (body) => ({
        url: "/api/auth/definir-senha",
        method: "POST",
        body,
      }),
    }),

    /** POST /api/auth/logout — limpa cookies httpOnly */
    logout: builder.mutation<void, void>({
      queryFn: async (_arg, { dispatch }, _extraOptions, baseQuery) => {
        await baseQuery({ url: "/api/auth/logout", method: "POST" });
        dispatch(clearUser());
        encryptedRemoveItem(USER_KEY);
        return { data: undefined };
      },
    }),

    /** GET /api/politica-senha/lookup (AllowAnonymous) */
    obterPoliticaSenha: builder.query<PoliticaSenhaResponse, void>({
      query: () => "/api/politica-senha/lookup",
    }),

    /** GET /api/auth/me */
    getMe: builder.query<AuthUser, void>({
      query: () => "/api/auth/me",
    }),
  }),
});

export const {
  useSignInMutation,
  useEsqueciSenhaMutation,
  useVerificarMfaMutation,
  useVerificarRecoveryCodeMutation,
  useRedefinirSenhaMutation,
  useDefinirSenhaMutation,
  useLogoutMutation,
  useObterPoliticaSenhaQuery,
  useLazyObterPoliticaSenhaQuery,
  useGetMeQuery,
  useLazyGetMeQuery,
} = authApi;
